import { useEffect, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '../store'
import { notificationService } from '../utils/notifications'
import { useNavigate } from 'react-router-dom'
import { addCommentToIssue, updateIssueInKanban, removeIssueFromKanban } from '../store/slices/issueSlice'
import { addNotification } from '../store/slices/uiSlice'
import { fetchProjects } from '../store/slices/projectSlice'
import { dispatchIssueNotification } from '../utils/issueNotificationDispatch'

interface IssueNotificationData {
  issue: {
    _id: string
    key: string
    title: string
    project?: string
  }
  projectId: string
  projectName?: string
  actionBy: {
    _id: string
    name: string
    email: string
  }
  involvedUsers: {
    assignee: string | null
    reporter: string | null
    watchers: string[]
  }
}

interface CommentNotificationData extends IssueNotificationData {
  comment: {
    _id: string
    content: string
    author: {
      _id: string
      name: string
      email: string
    }
  }
}

interface StatusChangeNotificationData extends IssueNotificationData {
  oldStatus: string
  newStatus: string
}

/**
 * Custom hook to handle issue-related socket events and show browser notifications
 * Only shows notifications for issues the current user is involved in
 */
export const useIssueNotifications = () => {
  const { socket, isConnected, joinProject } = useSocket()
  const { user } = useSelector((state: RootState) => state.auth)
  const { projects } = useSelector((state: RootState) => state.projects)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const handlersRef = useRef<Map<string, () => void>>(new Map())
  const joinedProjectsRef = useRef<Set<string>>(new Set())
  const recentNotificationsRef = useRef<Map<string, number>>(new Map()) // Track recent notifications to prevent duplicates

  // Fetch projects if not loaded
  useEffect(() => {
    if (projects.length === 0) {
      dispatch(fetchProjects({}))
    }
  }, [dispatch, projects.length])

  useEffect(() => {
    if (!socket || !isConnected || !user) {
      return
    }


    // Join all project rooms the user is a member of
    if (projects && projects.length > 0) {
      projects.forEach((project) => {
        const projectId = project._id
        if (!joinedProjectsRef.current.has(projectId)) {
          joinProject(projectId)
          joinedProjectsRef.current.add(projectId)
        }
      })
    } else {
    }

    // Helper function to check if user is involved in the issue
    const isUserInvolved = (data: IssueNotificationData): boolean => {
      const userId = user?._id
      if (!userId) {
        return false
      }

      // Some emitted events may omit involvedUsers entirely (a backend payload
      // gap) - fail safe to "not involved" rather than throwing.
      if (!data.involvedUsers) {
        return false
      }

      // Normalize IDs to strings for comparison
      const userIdStr = userId.toString()
      const assigneeStr = data.involvedUsers.assignee ? data.involvedUsers.assignee.toString() : null
      const reporterStr = data.involvedUsers.reporter ? data.involvedUsers.reporter.toString() : null
      const watchersStr = (data.involvedUsers.watchers || []).map(w => w ? w.toString() : '').filter(Boolean)

      const isAssignee = assigneeStr === userIdStr
      const isReporter = reporterStr === userIdStr
      const isWatcher = watchersStr.includes(userIdStr)

      const involved = isAssignee || isReporter || isWatcher

      return involved
    }

    // Helper function to check if the action was performed by the current user
    const isActionByCurrentUser = (data: IssueNotificationData): boolean => {
      const userId = user?._id
      if (!userId) return false
      // Normalize IDs to strings for comparison
      const userIdStr = userId.toString()
      const actionByStr = data.actionBy._id ? data.actionBy._id.toString() : ''
      return actionByStr === userIdStr
    }

    // Helper function to check if notification was recently shown (prevents duplicates)
    const wasRecentlyNotified = (key: string, type: string): boolean => {
      const notificationKey = `${type}-${key}`
      const lastTime = recentNotificationsRef.current.get(notificationKey) || 0
      const now = Date.now()
      const timeSinceLastNotification = now - lastTime
      
      // If same notification was shown in last 5 seconds, skip it
      if (timeSinceLastNotification < 5000) {
        return true
      }
      
      // Update the timestamp
      recentNotificationsRef.current.set(notificationKey, now)
      
      // Clean up old entries (older than 10 seconds)
      for (const [k, v] of recentNotificationsRef.current.entries()) {
        if (now - v > 10000) {
          recentNotificationsRef.current.delete(k)
        }
      }
      
      return false
    }

    // Handle comment added event
    const handleCommentAdded = (data: CommentNotificationData) => {
      // Don't update Redux if the current user added the comment (addComment.fulfilled already did)
      if (!isActionByCurrentUser(data)) {
        dispatch(addCommentToIssue({
          issueId: data.issue._id,
          comment: data.comment
        }))
      }

      // Don't notify if the action was performed by the current user
      if (isActionByCurrentUser(data)) return

      // Only notify if user is involved in the issue
      if (!isUserInvolved(data)) return

      // Check if we recently notified about this comment
      const notificationKey = `comment-${data.issue.key}-${data.comment._id}`
      if (wasRecentlyNotified(notificationKey, 'comment')) {
        return
      }

      dispatchIssueNotification({
        dispatch,
        category: 'comment',
        idPrefix: `comment-${data.comment._id}`,
        issueKey: data.issue.key,
        issueTitle: data.issue.title,
        projectId: data.projectId,
        projectName: data.projectName,
        actionBy: {
          name: data.comment.author.name,
          avatar: (data.comment.author as any).avatar
        },
        bellTitle: `New comment on ${data.issue.key}`,
        bellMessage: data.comment.content.substring(0, 100) + (data.comment.content.length > 100 ? '...' : ''),
        toastMessage: `${data.comment.author.name} commented on ${data.issue.key}: ${data.issue.title}`,
        notifyBrowser: (issueUrl) => notificationService.notifyComment(
          data.issue.key,
          data.issue.title,
          data.comment.author.name,
          data.comment.content,
          issueUrl
        )
      })
    }

    // Handle status change event
    const handleStatusChange = (data: StatusChangeNotificationData & { issue?: any }) => {
      // For status changes, we need to get the full issue data from the issue-updated event
      // The issue-status-changed event only has partial issue data
      // So we'll rely on the issue-updated event to update the Redux state
      // This handler is mainly for notifications

      // Don't notify if the action was performed by the current user
      if (isActionByCurrentUser(data)) return

      // Only notify if user is involved in the issue
      if (!isUserInvolved(data)) return

      // Check if we recently notified about this status change
      const notificationKey = `status-${data.issue.key}-${data.newStatus}`
      if (wasRecentlyNotified(notificationKey, 'status')) {
        return
      }

      dispatchIssueNotification({
        dispatch,
        category: 'status_change',
        idPrefix: `status-${data.issue._id}`,
        issueKey: data.issue.key,
        issueTitle: data.issue.title,
        projectId: data.projectId,
        projectName: data.projectName,
        actionBy: {
          name: data.actionBy.name,
          avatar: (data.actionBy as any).avatar
        },
        bellTitle: `Status changed: ${data.issue.key}`,
        bellMessage: `Changed status from "${data.oldStatus}" to "${data.newStatus}"`,
        toastMessage: `${data.actionBy.name} changed ${data.issue.key} status from "${data.oldStatus}" to "${data.newStatus}"`,
        notifyBrowser: (issueUrl) => notificationService.notifyStatusChange(
          data.issue.key,
          data.issue.title,
          data.oldStatus,
          data.newStatus,
          data.actionBy.name,
          issueUrl
        )
      })
    }

    // Handle issue updated event
    const handleIssueUpdated = (data: IssueNotificationData & { issue?: any }) => {
      
      // Update Redux state immediately if full issue data is provided
      if (data.issue && data.issue._id) {
        dispatch(updateIssueInKanban(data.issue))
      }

      // Don't notify if the action was performed by the current user
      if (isActionByCurrentUser(data)) {
        return
      }

      // Only notify if user is involved in the issue
      if (!isUserInvolved(data)) {
        return
      }

      // Skip notifications for issue-updated events that are likely duplicates
      // (e.g., status changes already handled by issue-status-changed)
      // We'll only show notifications for assignment changes here


      // Safety check: ensure we have issue key
      const issueKey = data.issue?.key || (data.issue as any)?.key
      const issueTitle = data.issue?.title || (data.issue as any)?.title
      if (!issueKey) {
        console.warn('Issue key not found in update event:', data)
        return
      }

      const userId = user?._id
      const userIdStr = userId ? userId.toString() : ''

      // Check if this is an assignment change
      const issueId = data.issue?._id || (data.issue as any)?._id
      const assigneeStr = data.involvedUsers.assignee ? data.involvedUsers.assignee.toString() : null
      if (assigneeStr === userIdStr) {
        // Check if we recently notified about this assignment
        const notificationKey = `assignment-${issueKey}`
        if (wasRecentlyNotified(notificationKey, 'assignment')) {
          return
        }

        dispatchIssueNotification({
          dispatch,
          category: 'assignment',
          idPrefix: `assignment-${issueId || Date.now()}`,
          issueKey,
          issueTitle,
          projectId: data.projectId,
          projectName: data.projectName,
          actionBy: {
            name: data.actionBy.name,
            avatar: (data.actionBy as any).avatar
          },
          bellTitle: `Assigned to you: ${issueKey}`,
          bellMessage: `Assigned "${issueTitle}" to you`,
          toastMessage: `You were assigned to ${issueKey}: ${issueTitle}`,
          notifyBrowser: (issueUrl) => notificationService.notifyAssignment(
            issueKey,
            issueTitle,
            data.actionBy.name,
            issueUrl
          )
        })
      }
      // Don't show generic update notifications here - they're likely duplicates
      // of status changes or comments that are handled by their specific handlers
    }

    // Handle issue created event
    const handleIssueCreated = (data: IssueNotificationData & { issue?: any }) => {
      
      // Update Redux state if full issue data is provided
      if (data.issue && data.issue._id) {
        // Issue will be added via the createIssue fulfilled action, but we can update kanban if needed
      }

      // Don't notify if the action was performed by the current user
      if (isActionByCurrentUser(data)) {
        return
      }

      // Only notify if user is involved in the issue (assigned or watching)
      if (!isUserInvolved(data)) {
        return
      }


      // Safety check: ensure we have issue key
      const issueKey = data.issue?.key || (data.issue as any)?.key
      const issueTitle = data.issue?.title || (data.issue as any)?.title
      const issueId = data.issue?._id || (data.issue as any)?._id
      if (!issueKey) {
        console.warn('Issue key not found in create event:', data)
        return
      }

      dispatchIssueNotification({
        dispatch,
        category: 'new_issue',
        idPrefix: `new-${issueId || Date.now()}`,
        issueKey,
        issueTitle,
        projectId: data.projectId,
        projectName: data.projectName,
        actionBy: {
          name: data.actionBy.name,
          avatar: (data.actionBy as any).avatar
        },
        bellTitle: `New issue: ${issueKey}`,
        bellMessage: `Created "${issueTitle}"`,
        toastMessage: `New issue ${issueKey}: ${issueTitle}`,
        notifyBrowser: (issueUrl) => notificationService.notifyNewIssue(
          issueKey,
          issueTitle,
          data.actionBy.name,
          issueUrl
        )
      })
    }

    // Handle issue deleted event
    const handleIssueDeleted = (data: IssueNotificationData & { issueKey?: string; issueTitle?: string; issueId?: string; reason?: string }) => {
      // Update Redux state immediately
      const removedId = data.issueId || data.issue?._id
      if (removedId) {
        dispatch(removeIssueFromKanban(removedId))
      }

      // An issue that just became hidden from the client is removed silently -
      // no "deleted" toast, since it wasn't actually deleted.
      if (data.reason === 'hidden-from-client') return

      // Don't notify if the action was performed by the current user
      if (isActionByCurrentUser(data)) return

      // Only notify if user is involved in the issue
      if (!isUserInvolved(data)) return

      const issueKey = data.issueKey || data.issue?.key || (data.issue as any)?.key
      const issueTitle = data.issueTitle || data.issue?.title || (data.issue as any)?.title
      
      // Show in-app toast
      dispatch(addNotification({
        type: 'warning',
        message: `${data.actionBy.name} deleted ${issueKey}`
      }))
    }

    // Register event listeners
    socket.on('issue-comment-added', handleCommentAdded)
    socket.on('issue-status-changed', handleStatusChange)
    socket.on('issue-updated', handleIssueUpdated)
    socket.on('issue-created', handleIssueCreated)
    socket.on('issue-deleted', handleIssueDeleted)

    // Store cleanup functions
    const cleanup = () => {
      socket.off('issue-comment-added', handleCommentAdded)
      socket.off('issue-status-changed', handleStatusChange)
      socket.off('issue-updated', handleIssueUpdated)
      socket.off('issue-created', handleIssueCreated)
      socket.off('issue-deleted', handleIssueDeleted)
    }

    handlersRef.current.set('cleanup', cleanup)

    return cleanup
  }, [socket, isConnected, user, navigate, dispatch, projects, joinProject])

  // Handle navigation events from notifications
  useEffect(() => {
    const handleNavigate = (event: CustomEvent) => {
      const route = event.detail
      if (route) {
        navigate(route)
      }
    }

    window.addEventListener('navigate', handleNavigate as EventListener)

    return () => {
      window.removeEventListener('navigate', handleNavigate as EventListener)
    }
  }, [navigate])
}

